import { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, HandCoins, Users, Truck, Package, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/PageHeader";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return (
      <div>
        <PageHeader title="Dashboard" subtitle="Trading overview at a glance" />
        <div className="px-8 py-8 text-slate-500">Loading…</div>
      </div>
    );
  }

  const stats = [
    { label: "Total Sales", value: inr(data.totals.sales), icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
    { label: "Total Purchases", value: inr(data.totals.purchases), icon: TrendingDown, color: "text-sky-600 bg-sky-50" },
    { label: "Receivable", value: inr(data.totals.receivable), icon: HandCoins, color: "text-indigo-600 bg-indigo-50" },
    { label: "Payable", value: inr(data.totals.payable), icon: Wallet, color: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Trading overview at a glance" />
      <div className="px-8 py-6 space-y-6" data-testid="dashboard-content">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <Card key={s.label} className="p-5 border-slate-200 card-hover">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div className="mt-4 text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">
                {s.label}
              </div>
              <div className="mt-1 font-display text-2xl font-bold number-cell">{s.value}</div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-6 border-slate-200 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-bold">Sales vs Purchases</h3>
                <p className="text-xs text-slate-500">Last 6 months</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="sales" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="purchases" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 space-y-4">
            <h3 className="font-display text-lg font-bold">At a glance</h3>
            <Row icon={Users} label="Customers" value={data.counts.customers} />
            <Row icon={Truck} label="Suppliers" value={data.counts.suppliers} />
            <Row icon={Package} label="Items" value={data.counts.items} />
            <Row icon={AlertTriangle} label="Overdue Receivables" value={data.overdue.receivable} accent="text-rose-600" />
            <Row icon={AlertTriangle} label="Overdue Payables" value={data.overdue.payable} accent="text-amber-600" />
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-6 border-slate-200">
            <h3 className="font-display text-lg font-bold mb-4">Recent Invoices</h3>
            {data.recent_invoices.length === 0 ? (
              <p className="text-sm text-slate-500">No invoices yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.recent_invoices.map((d) => (
                  <li key={d.doc_id} className="py-3 flex items-center justify-between">
                    <div>
                      <Link to={`/documents/${d.doc_id}`} className="font-medium text-slate-900 hover:text-indigo-700">
                        {d.number}
                      </Link>
                      <div className="text-xs text-slate-500">{d.party_name} · {String(d.date).slice(0,10)}</div>
                    </div>
                    <div className="text-right">
                      <div className="number-cell font-semibold">{inr(d.total)}</div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">{d.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card className="p-6 border-slate-200">
            <h3 className="font-display text-lg font-bold mb-4">Low / Out of Stock</h3>
            {data.low_stock.length === 0 ? (
              <p className="text-sm text-slate-500">All items look healthy.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.low_stock.map((i) => (
                  <li key={i.item_id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{i.name}</div>
                      <div className="text-xs text-slate-500">{i.sku || i.hsn || "—"}</div>
                    </div>
                    <div className={`number-cell font-semibold ${i.stock <= 0 ? "text-rose-600" : "text-amber-600"}`}>
                      {i.stock} {i.unit}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 text-slate-600 text-sm">
        <Icon className="w-4 h-4 text-slate-400" /> {label}
      </div>
      <div className={`font-display font-bold text-lg number-cell ${accent || ""}`}>{value}</div>
    </div>
  );
}
